import { useEffect, useRef } from 'react'

type Point = { x: number; y: number; baseX: number; baseY: number; vx: number; vy: number }

// A generative canvas hero, the technique epiminds builds their whole front
// door around -- sample a shape into a point cloud, then let the points
// live. Here the shape is a signal pulse (not a face; this is an ads
// account, not a portrait), and the points drift on their own and give way
// to the cursor. Reduced-motion gets the assembled shape with no drift and
// no pointer tracking, since neither Lenis nor Motion touches canvas RAF
// loops on their own.
function buildPulsePoints(width: number, height: number, dpr: number): Point[] {
  if (width <= 0 || height <= 0) return []
  const off = document.createElement('canvas')
  off.width = width
  off.height = height
  const ctx = off.getContext('2d')
  if (!ctx) return []

  ctx.clearRect(0, 0, width, height)
  ctx.strokeStyle = '#fff'
  ctx.lineWidth = Math.max(3, height * 0.035)
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  const midY = height / 2
  const w = width
  ctx.beginPath()
  ctx.moveTo(0, midY)
  ctx.lineTo(w * 0.32, midY)
  ctx.lineTo(w * 0.4, midY - height * 0.34)
  ctx.lineTo(w * 0.47, midY + height * 0.4)
  ctx.lineTo(w * 0.53, midY - height * 0.14)
  ctx.lineTo(w * 0.6, midY)
  ctx.lineTo(w, midY)
  ctx.stroke()

  const data = ctx.getImageData(0, 0, width, height).data
  // Spaced out in CSS pixels, not device pixels -- sampling every couple of
  // *device* pixels on a high-dpr phone packed the squares tight enough to
  // overlap into what read as one solid stroke instead of a particle line.
  const stepCss = Math.max(3, Math.round(width / dpr / 170))
  const step = stepCss * dpr
  const points: Point[] = []
  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      const alpha = data[(y * width + x) * 4 + 3]
      if (alpha > 120) {
        points.push({ x, y, baseX: x, baseY: y, vx: 0, vy: 0 })
      }
    }
  }
  return points
}

export function ParticleSignal({ reduceMotion }: { reduceMotion: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const pointsRef = useRef<Point[]>([])
  const mouseRef = useRef({ x: -9999, y: -9999 })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let raf = 0
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const start = performance.now()
    let dotSize = dpr * 1.4

    function resize() {
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      pointsRef.current = buildPulsePoints(canvas.width, canvas.height, dpr)
      // Keeps the dot visibly smaller than the gap between dots at any
      // screen density, so the line reads as particles up close too.
      const stepCss = Math.max(3, Math.round(rect.width / 170))
      dotSize = Math.min(2.2, stepCss * 0.45) * dpr
    }

    resize()
    // Setting canvas.width/height (inside resize) always clears the bitmap,
    // even to the same size. That's invisible while the RAF loop is
    // running, but for reduced-motion visitors nothing repaints
    // afterward -- a resize (address-bar collapse on scroll, orientation
    // change) would otherwise leave the canvas permanently blank.
    function onWindowResize() {
      resize()
      cancelAnimationFrame(raf)
      draw()
    }
    window.addEventListener('resize', onWindowResize)

    function onPointerMove(e: PointerEvent) {
      const rect = canvas!.getBoundingClientRect()
      mouseRef.current = { x: (e.clientX - rect.left) * dpr, y: (e.clientY - rect.top) * dpr }
    }
    function onPointerLeave() {
      mouseRef.current = { x: -9999, y: -9999 }
    }
    canvas.addEventListener('pointermove', onPointerMove)
    canvas.addEventListener('pointerleave', onPointerLeave)

    // A tap doesn't reliably generate the sustained pointermove drag a
    // mouse hover does before the browser claims the gesture for page
    // scroll, so a finger sweeping the hero on a phone saw no reaction.
    // A poke on touchstart gives immediate, visible feedback without
    // hijacking scroll (only touchstart is handled, never touchmove).
    function onTouchStart(e: TouchEvent) {
      const touch = e.touches[0]
      if (!touch) return
      const rect = canvas!.getBoundingClientRect()
      const tx = (touch.clientX - rect.left) * dpr
      const ty = (touch.clientY - rect.top) * dpr
      const radius = canvas!.width * 0.11
      for (const p of pointsRef.current) {
        const dx = p.baseX - tx
        const dy = p.baseY - ty
        const dist = Math.hypot(dx, dy)
        if (dist < radius) {
          const force = (1 - dist / radius) * 9
          p.vx += (dx / (dist || 1)) * force
          p.vy += (dy / (dist || 1)) * force
        }
      }
    }
    canvas.addEventListener('touchstart', onTouchStart, { passive: true })

    const accent = getComputedStyle(document.documentElement).getPropertyValue('--accent-hi').trim() || '#f0bd7a'
    const dim = getComputedStyle(document.documentElement).getPropertyValue('--n-500').trim() || '#948a78'

    const maxSpeed = 14
    // A slow glint that sweeps the line back and forth on its own, so the
    // signal reads as alive even before anyone touches it -- previously it
    // sat perfectly still until a cursor arrived.
    const sweepSeconds = 5

    function draw() {
      if (!ctx || !canvas) return
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      const mouse = mouseRef.current
      const radius = canvas.width * 0.06

      const t = (performance.now() - start) / 1000
      const tri = reduceMotion ? 0 : 1 - Math.abs(((t / sweepSeconds) % 2) - 1)
      const scanX = tri * canvas.width
      const band = canvas.width * 0.09

      for (const p of pointsRef.current) {
        if (!reduceMotion) {
          const dx = p.x - mouse.x
          const dy = p.y - mouse.y
          const dist = Math.hypot(dx, dy)
          if (dist < radius) {
            const force = (1 - dist / radius) * 6
            p.vx += (dx / (dist || 1)) * force
            p.vy += (dy / (dist || 1)) * force
          }
          p.vx += (p.baseX - p.x) * 0.02
          p.vy += (p.baseY - p.y) * 0.02
          p.vx *= 0.82
          p.vy *= 0.82
          const speed = Math.hypot(p.vx, p.vy)
          if (speed > maxSpeed) {
            p.vx = (p.vx / speed) * maxSpeed
            p.vy = (p.vy / speed) * maxSpeed
          }
          p.x += p.vx
          p.y += p.vy
        } else {
          p.x = p.baseX
          p.y = p.baseY
        }

        const drifted = Math.hypot(p.x - p.baseX, p.y - p.baseY) > 1.5
        ctx.fillStyle = drifted ? accent : dim
        ctx.fillRect(p.x, p.y, dotSize, dotSize)

        if (!drifted && !reduceMotion) {
          const dxScan = Math.abs(p.baseX - scanX)
          if (dxScan < band) {
            ctx.globalAlpha = 1 - dxScan / band
            ctx.fillStyle = accent
            ctx.fillRect(p.x, p.y, dotSize, dotSize)
            ctx.globalAlpha = 1
          }
        }
      }

      if (!reduceMotion) raf = requestAnimationFrame(draw)
    }

    draw()

    return () => {
      window.removeEventListener('resize', onWindowResize)
      canvas.removeEventListener('pointermove', onPointerMove)
      canvas.removeEventListener('pointerleave', onPointerLeave)
      canvas.removeEventListener('touchstart', onTouchStart)
      cancelAnimationFrame(raf)
    }
  }, [reduceMotion])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="block h-[220px] w-full min-[900px]:h-[300px]"
    />
  )
}
